import { readIntegerString } from './live-bucket-core.js';

/**
 * Patch the traded outcome's current BC mark only from the versioned payload
 * that separates execution average from post-reserve spot. During a rolling
 * deploy, origin/main producers omit `executionPriceD9` and put execution
 * average in `postOutcomePriceD9`; those events must leave the cached mark
 * unchanged until the normal authoritative REST reconciliation.
 */
function positiveIntegerString(raw: Record<string, unknown>, key: string): string | null {
  const value = readIntegerString(raw, key);
  if (value === null) return null;
  const parsed = BigInt(value);
  return parsed > 0n ? parsed.toString() : null;
}

export function applyBcTradeMarkToMarket<
  T extends {
    readonly outcomes?: ReadonlyArray<{
      readonly outcomeIndex?: number;
      readonly price?: string;
    }>;
  },
>(prev: T, raw: Record<string, unknown>): T {
  const executionPriceD9 = positiveIntegerString(raw, 'executionPriceD9');
  if (executionPriceD9 === null) return prev;

  const postOutcomePriceD9 = positiveIntegerString(raw, 'postOutcomePriceD9');
  const outcomeIdxRaw = readIntegerString(raw, 'outcomeIdx');
  if (outcomeIdxRaw === null) return prev;
  const outcomeIdxBigInt = BigInt(outcomeIdxRaw);
  if (outcomeIdxBigInt < 0n || outcomeIdxBigInt > BigInt(Number.MAX_SAFE_INTEGER)) return prev;
  const outcomeIdx = Number(outcomeIdxBigInt);
  const outcomes = prev.outcomes;
  if (postOutcomePriceD9 === null || !Array.isArray(outcomes) || outcomeIdx < 0) return prev;
  const outcomePosition = outcomes.findIndex((outcome) => outcome.outcomeIndex === outcomeIdx);
  if (outcomePosition < 0 || outcomes[outcomePosition]?.price === postOutcomePriceD9) return prev;

  return {
    ...prev,
    outcomes: outcomes.map((outcome, index) =>
      index === outcomePosition ? { ...outcome, price: postOutcomePriceD9 } : outcome,
    ),
  };
}
